import React, {
  useState,
  useEffect,
  useCallback
} from 'react';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Close as CloseIcon, Search as SearchIcon } from '@material-ui/icons';
import DealershipIcon from './DealershipIcon';
import { blue } from '@material-ui/core/colors';
import {
  Avatar,
  Box,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import useDealers from 'src/hooks/useDealers';
import accountService from '../services/accountService';


const useStyles = makeStyles((theme) => ({
  avatar: {
    backgroundColor: blue[600],
    color: blue[600],
  },
  closeBtn: {
    marginRight: -10
  },
  dialogTitle: {
    alignSelf: 'center'
  },
  dealerList: {
    minHeight: 360,
    maxHeight: 360,
    overflowY: 'auto'
  },
  list: {
    padding: 0,
  },
  listItem: {
    padding: theme.spacing(1, 2),
  },
  listItemSelected: {
    backgroundColor: 'rgb(0, 177, 255, 0.15) !important'
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(8),
    },
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(1.5)
    }
  },
  text: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}));

function LoadingListItem({
  index,
  style
}) {
  const classes = useStyles();
  const name = 'Loading...';
  return (
    <ListItem
      button
      selected={false}
      className={clsx(
        classes.listItem,
      )}
      ContainerComponent="div"
      style={style}
      key={index}
    >
      <ListItemAvatar>
        <Avatar className={classes.avatar}>
          <DealershipIcon color={blue[600]} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        title={name}
        classes={{ primary: classes.text }}
        primary={name}
      />
    </ListItem>
  );
}
LoadingListItem.propTypes = {
  index: PropTypes.string.isRequired,
  style: PropTypes.object,
};

function DealerCodeListItem({
  dealer = {},
  index,
  isSelected,
  toggleSelected,
  style
}) {
  const classes = useStyles();
  const { name, dealerCode } = dealer;

  return (
    <ListItem
      button
      selected={isSelected}
      onClick={toggleSelected}
      className={clsx(
        classes.listItem,
        isSelected && classes.listItemSelected
      )}
      ContainerComponent="div"
      style={style}
      key={index}
    >
      <ListItemAvatar>
        <Avatar className={classes.avatar}>
          <DealershipIcon color={blue[600]} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        title={name}
        classes={{ primary: classes.text }}
        primary={name}
        secondary={dealerCode}
      />
    </ListItem>
  );
}

DealerCodeListItem.propTypes = {
  dealer: PropTypes.object.isRequired,
  index: PropTypes.string.isRequired,
  style: PropTypes.object,
  isSelected: PropTypes.bool.isRequired,
  toggleSelected: PropTypes.func,
};

DealerCodeListItem.defaultProps = {
  toggleSelected: () => { },
  style: {},
};


const pageSize = 20;

function DealerCodeDialog({ onClose, isOpen }) {
  const classes = useStyles();

  const [firstLoadComplete, setFirstLoadComplete] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);

  const { user } = useSelector(state => state.account);
  const [search, setSearch] = useState(user?.dealerFetchParams?.search ?? '');

  const { selectDealer } = useDealers();
  const [dealerState, setDealerState] = useState({
    page: user?.dealerFetchParams?.page ?? 1,
    search: user?.dealerFetchParams?.search ?? '',
    dealers: user.agents ?? [],
    finishedLoading: false,
    itemCount: (user.agents ?? []).length + 1,
  });

  const handleClose = () => {
    onClose();
  };

  const toggleSelectedDealer = (dealerCode) => {
    selectDealer(dealerCode, dealerState.dealers);
    handleClose();
  }

  useEffect(() => setFirstLoadComplete(true), []);

  useEffect(() => {
    if (firstLoadComplete) {
      handleFilterDealers(search);
    }
  }, [search]);

  const handleFilterDealers = useCallback(debounce((value) => {
    setDealerState({
      page: 1,
      search: value,
      dealers: [],
      finishedLoading: false,
      itemCount: 1,
    });
  }, 300), []);

  useEffect(() => {
    const dealers = user?.agents?.filter(d => d.active) ?? [];
    setSearch('');
    setDealerState({
      page: 1,
      search: '',
      dealers,
      finishedLoading: false,
      itemCount: dealers.length + 1,
    });
  }, [user, isOpen]);

  const sortedDealers = dealerState.dealers.slice().sort((a, b) => Number(b.active) - Number(a.active));

  useEffect(() => {
    if (isOpen) {
      const params = { ...dealerState };
      const fetchDealers = async () => {
        setIsFetchingData(true);
        const offset = (params.page - 1) * pageSize;
        const newItems = await accountService.getDealerships(user.isSuperUser, pageSize, offset, params.search);

        const hasFinishedLoading = newItems.dealerships.length < pageSize;
        setDealerState(prev => {
          if (prev.search === params.search) {
            return {
              ...prev,
              isFetchingData: false,
              finishedLoading: hasFinishedLoading,
              dealers: [...prev.dealers, ...newItems.dealerships],
              itemCount: hasFinishedLoading ? prev.dealers.length + newItems.dealerships.length : prev.dealers.length + newItems.dealerships.length + 1,
            };
          }
          return prev;
        });

        setIsFetchingData(false);
      };
      fetchDealers();
    }
  }, [
    isOpen,
    dealerState.page,
    dealerState.search,
    accountService,
    user.isSuperUser
  ]);

  const loadMoreItems = () => {
    if (!isFetchingData) {
      setIsFetchingData(true);
      setDealerState(prev => ({ ...prev, page: prev.page + 1 }));
    }
  }

  const isItemLoaded = index => {
    return index < sortedDealers.length
  };

  return (
    <Dialog className={classes.dialog} onClose={handleClose} open={isOpen} maxWidth="xs" fullWidth>
      <Box px={2} py={1} display="flex" width="100%" justifyContent="space-between">
        <Typography variant="h4" className={classes.dialogTitle}>
          Dealerships
        </Typography>
        <IconButton onClick={handleClose} className={classes.closeBtn}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box p={2}>
        <TextField
          fullWidth
          className={classes.searchField}
          variant="outlined"
          placeholder="Search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          onChange={e => setSearch(e.target.value)}
          value={search}
        />
      </Box>
      <Divider />
      <Box className={classes.dealerList}>
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={dealerState.itemCount}
          loadMoreItems={loadMoreItems}
        >
          {({ onItemsRendered, ref }) => (
            <FixedSizeList
              onItemsRendered={onItemsRendered}
              ref={ref}
              className={classes.list}
              height={360}
              width={444}
              itemSize={72}
              itemCount={dealerState.itemCount}
            >
              {({ index, style }) => index < sortedDealers.length ? (
                <DealerCodeListItem
                  id={`agent-list-item-${index}`}
                  dealer={sortedDealers[index]}
                  index={index}
                  style={style}
                  isSelected={sortedDealers[index].active}
                  toggleSelected={
                    () => toggleSelectedDealer(sortedDealers[index].dealerCode)
                  }
                />
              ) : (
                <LoadingListItem
                  id={`agent-list-item-${index}`}
                  index={index}
                  style={style}
                />
              )}
            </FixedSizeList>
          )}
        </InfiniteLoader>
      </Box>
    </Dialog>
  );
}

DealerCodeDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool
};

DealerCodeDialog.defaultProps = {
  onClose: () => { },
};

export default DealerCodeDialog;
